import {
    ActionType, CORPORATE_STRUCTURE_DASHBOARD_FILTERING_ENABLEDNESS_PROPERTIES_MAP, CartesianSerieType, ComparisonBarSerieType, ComparisonColumnSerieType, DashboardBlockType,
    DashboardCartesianAxis, DashboardPageBlockSerieOrder, DashboardStrategicActionViewType, FORMULA_INDICATOR_ELEMENT_TYPES, MeetingStatus, NetworkActionPrefix,
    PerformanceIndicatorDimension, PeriodType, RequestStatus
} from '../utils/constants';
import defaults from '../api/defaults';
import { findIndicatorData, indicatorDrillDataID, stringifyCorporateStructureLink } from '../api/hz';
import { deepAssign, deepClone } from '../utils';
import {
    combineReducers, networkActionReducer, networkEntityAddAllReducer, networkEntityAddReducer, networkEntityDeleteAllReducer, networkEntityDeleteReducer,
    networkEntityEditAllReducer, networkEntityEditReducer, networkEntityLoadReducer, networkObjectLoadReducer, networkObjectVoidReducer, networkPaginationLoadReducer,
    optimisticNetworkEntityLoadReducer, paginationSlicePopReducer, paginationSlicePushReducer, pipeReducers, sortableEntitiesLifecycleReducer, synchronizableEnumReducer
} from '../utils/reducerFactory';

function index(state, action, id) {
    return id !== undefined ? (id = state.getIndexByID(id)) !== -1 ? id : state.length : undefined;
}

function indicatorReportID(indicatorID, filters) {
    return `${indicatorID}_${filters.PeriodTypeID || 0}_${stringifyCorporateStructureLink(filters.CorporateStructureLink)}`;
}

const keyVariables = {
    pageIndex: (state, action) =>
        action.type === ActionType.ADD_ENTITY_CHANGE || action.type === ActionType.DROP_ENTITY_CHANGE ?
            (state = state.getIndexByID(action.change.OptionalDetails.PageID)) !== -1 ? state : undefined :
            (action.type === ActionType.CLONE_DASHBOARD_PAGE || action.type === ActionType.CLONE_DASHBOARD_PAGE_PENDING || action.type === ActionType.CLONE_DASHBOARD_PAGE_SUCCESS || action.type === ActionType.CLONE_DASHBOARD_PAGE_FAIL) && !action.targetCompanyID ?
                state.findIndex(page => page && page.Custom.sourcePageID === action.sourcePageID) :
                (action.type === ActionType.CLONE_DASHBOARD_BLOCK || action.type === ActionType.CLONE_DASHBOARD_BLOCK_PENDING || action.type === ActionType.CLONE_DASHBOARD_BLOCK_SUCCESS || action.type === ActionType.CLONE_DASHBOARD_BLOCK_FAIL) && !action.targetCompanyID ?
                    index(state, action, action.sourcePageID) :
                    (action.type === ActionType.ADD_DASHBOARD_PAGE_FROM_TEMPLATE || action.type === ActionType.ADD_DASHBOARD_PAGE_FROM_TEMPLATE_PENDING || action.type === ActionType.ADD_DASHBOARD_PAGE_FROM_TEMPLATE_SUCCESS || action.type === ActionType.ADD_DASHBOARD_PAGE_FROM_TEMPLATE_FAIL) && !action.targetCompanyID ?
                        index(state, action, action.placeholder.ID) :
                        index(state, action, action.pageID),
    blockIndex: (state, action) =>
        (action.type === ActionType.CLONE_DASHBOARD_BLOCK || action.type === ActionType.CLONE_DASHBOARD_BLOCK_PENDING || action.type === ActionType.CLONE_DASHBOARD_BLOCK_SUCCESS || action.type === ActionType.CLONE_DASHBOARD_BLOCK_FAIL) && !action.targetCompanyID ?
            state.findIndex(block => block && block.Custom.sourceBlockID === action.sourceBlockID) :
            (action.type === ActionType.ADD_DASHBOARD_BLOCK_FROM_TEMPLATE || action.type === ActionType.ADD_DASHBOARD_BLOCK_FROM_TEMPLATE_PENDING || action.type === ActionType.ADD_DASHBOARD_BLOCK_FROM_TEMPLATE_SUCCESS || action.type === ActionType.ADD_DASHBOARD_BLOCK_FROM_TEMPLATE_FAIL) && !action.targetCompanyID ?
                index(state, action, action.placeholder.ID) :
                index(state, action, action.blockID),
    cartesianSerieIndex: (state, action) => index(state, action, action.cartesianSerieID),
    cartesianSerieGroupingIndex: (state, action) => index(state, action, action.cartesianSerieGroupingID),
    cartesianSerieMilestoneIndex: (state, action) => index(state, action, action.cartesianSerieMilestoneID),
    groupIndex: (state, action) => index(state, action, action.dashboardGroupID),
    homeSettingsIndex: (state, action) => index(state, action, action.dashboardPageHomeSettingsID),
    comparisonColumnSerieIndex: (state, action) => index(state, action, action.comparisonColumnSerieID),
    comparisonBarSerieIndex: (state, action) => index(state, action, action.comparisonBarSerieID),
    pieSerieIndex: (state, action) => index(state, action, action.pieSerieID),
    numberSerieIndex: (state, action) => index(state, action, action.numberSerieID),
    solidGaugeSerieIndex: (state, action) => index(state, action, action.solidGaugeSerieID),
    tableSerieRowIndex: (state, action) => index(state, action, action.tableSerieRowID),
    serieRowExpandedIndicatorIndex: (state, action) => index(state, action, action.serieRowExpandedIndicatorID),
    tableComparisonRowIndex: (state, action) => index(state, action, action.tableComparisonRowID),
    commentIndex: (state, action) => index(state, action, action.commentID),
    pageCloneStatusIndex: (state, action) =>
        action.type === ActionType.CLONE_DASHBOARD_PAGE || action.type === ActionType.CLONE_DASHBOARD_PAGE_PENDING || action.type === ActionType.CLONE_DASHBOARD_PAGE_SUCCESS || action.type === ActionType.CLONE_DASHBOARD_PAGE_FAIL ?
            (action = (state.length ? state : []).findIndex(page => page.Custom.sourcePageID === action.sourcePageID && (page.Custom.targetCompanyID === action.targetCompanyID || !page.Custom.targetCompanyID && !action.targetCompanyID))) !== -1 ?
                action :
                state.length || 0 :
            undefined,
    blockCloneStatusIndex: (state, action) =>
        action.type === ActionType.CLONE_DASHBOARD_BLOCK || action.type === ActionType.CLONE_DASHBOARD_BLOCK_PENDING || action.type === ActionType.CLONE_DASHBOARD_BLOCK_SUCCESS || action.type === ActionType.CLONE_DASHBOARD_BLOCK_FAIL ?
            (action = (state.length ? state : []).findIndex(block => block.Custom.sourcePageID === action.sourcePageID && block.Custom.sourceBlockID === action.sourceBlockID && (block.Custom.targetCompanyID === action.targetCompanyID || !block.Custom.targetCompanyID && !action.targetCompanyID))) !== -1 ?
                action :
                state.length || 0 :
            undefined,
    regionIndex: (state, action) => index(state, action, action.regionID),
    indicatorsDrillDataKey: (state, action) => action.filters ? indicatorDrillDataID(action.filters) : undefined
};

function resetIndicatorsDataReducer(state, action) {
    if (!state ||
        action.type === ActionType.VOID_DASHBOARD_INDICATOR_DATA ||
        action.type === ActionType.CLEAR_PERFORMANCE_INDICATOR_DATA_SUCCESS ||
        action.type === ActionType.DELETE_PERFORMANCE_INDICATOR_SUCCESS ||
        action.type === ActionType.IMPORT_FORECAST_SPREADSHEET_SUCCESS ||
        action.type === ActionType.SPLIT_FINANCIAL_INDICATOR_TO_SUB_ACCOUNTS_SUCCESS ||
        action.type === ActionType.COPY_PERFORMANCE_INDICATOR_DATA_SUCCESS ||
        action.type === ActionType.MERGE_FORMULA_ITEMS_SUCCESS && action.calculate ||
        action.type === ActionType.ADD_STRATEGIC_PROGRAM_STATUS_UPDATE_SUCCESS ||
        action.type === ActionType.EDIT_STRATEGIC_PROGRAM_STATUS_UPDATE_SUCCESS ||
        action.type === ActionType.ADD_STRATEGIC_GOAL_METRIC_SUCCESS ||
        action.type === ActionType.EDIT_STRATEGIC_GOAL_METRIC_SUCCESS ||
        action.type === ActionType.DELETE_STRATEGIC_GOAL_METRIC_SUCCESS ||
        action.type === ActionType.ADD_STRATEGIC_PROGRAM_METRIC_SUCCESS ||
        action.type === ActionType.EDIT_STRATEGIC_PROGRAM_METRIC_SUCCESS ||
        action.type === ActionType.DELETE_STRATEGIC_PROGRAM_METRIC_SUCCESS ||
        action.type === ActionType.AGGREGATE_SHEET_FORECAST_FILE_DATA_SUCCESS && !action.preview ||
        action.type === ActionType.IMPORT_AND_PROCESS_GOOGLE_SHEETS_DATA_SUCCESS ||
        action.type === ActionType.REPLACE_FINANCIAL_INDICATOR_GROUP_SUCCESS ||
        action.type === 'EDIT_CASHFLOW_FORECAST_SETTINGS' && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'RECALCULATE_CASHFLOW_FORECAST' && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'AGGREGATE_FORECAST_MODEL_DATA' && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'AGGREGATE_FORECAST_MODEL_AGGREGATION_LEVEL_DATA' && !action.payload.preview && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'ADD_FORECAST_MODEL_DATA_ENTRY' && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'EDIT_FORECAST_MODEL_DATA_ENTRY' && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'IMPORT_FORECAST_MODEL_DATA_ENTRY_DATA' && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'DELETE_FORECAST_MODEL_DATA_ENTRY' && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'CALCULATE_FORESIGHT_SCENARIO_CASHFLOW' && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'CALCULATE_FORESIGHT_SCENARIO_FORECAST' && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'COPY_CAPITAL_ITEM_VALUES_FROM_SCENARIO' && action.payload.status === RequestStatus.SUCCEEDED ||
        action.type === 'COPY_BUDGET_FROM_FORESIGHT_SCENARIO_ELEMENT_SOURCE_INDICATOR' && action.payload.status === RequestStatus.SUCCEEDED
    ) {
        if (action.immediately === false) {
            if (action.indicatorIDs) {
                // TODO it could also be more precise with period and corporate structure link,
                // but there is no such stuff stored in data reports currently
                return state && deepAssign(state, state.map(data => !action.indicatorIDs.includes(data.IndicatorID) ? data : { Custom: { expires: new Date() } }));
            }

            return state && deepAssign(state, state.map(() => ({ Custom: { expires: new Date() } }))) || [];
        }
        if (action.soft) {
            if (action.indicatorIDs) {
                // TODO it could also be more precise with period and corporate structure link,
                // but there is no such stuff stored in data reports currently
                return state && deepAssign(state, state.map(data => !action.indicatorIDs.includes(data.IndicatorID) ? data : { Custom: { status: RequestStatus.UNSYNCED } }));
            }

            return state && deepAssign(state, state.map(() => ({ Custom: { status: RequestStatus.UNSYNCED } }))) || [];
        }
        else {
            if (action.indicatorIDs) {
                // TODO it could also be more precise with period and corporate structure link,
                // but there is no such stuff stored in data reports currently
                return state?.filter(data => !action.indicatorIDs.includes(data.IndicatorID));
            }

            return [];
        }
    }

    if (action.type === ActionType.EDIT_ALL_ERP_SYNC_PRODUCTS_SUCCESS) {
        return state.filter(data => action.erpSyncProducts.some(template => data.CorporateStructureLink.ProductID === template.ParentGroupID));
    }
    if (action.type === ActionType.EDIT_ALL_ERP_SYNC_CUSTOMERS_SUCCESS) {
        return state.filter(data => action.erpSyncCustomers.some(template => data.CorporateStructureLink.CustomerID === template.ParentGroupID));
    }

    if (action.type === ActionType.CLEAR_PIPEDRIVE_PERFORMANCE_INDICATOR_DATA_SUCCESS && action.indicatorIDs) {
        return state.filter(data => !action.indicatorIDs.includes(data.IndicatorID));
    }

    return state;
}

const orderedPeriodTypes = [PeriodType.DAY, PeriodType.WEEK, PeriodType.MONTH, PeriodType.QUARTER, PeriodType.YEAR];

function calculateDefaultPeriodTypeID(state, block) {
    if (block.PeriodTypeID) {
        return block.PeriodTypeID;
    }

    // TODO would it be easier to reload changed block than to duplicate DefaultPeriod calculation on server and client side?
    // currently this is a copy of DashboardPageBlockDataAccess.fillDefaultPeriod

    switch (block.TypeID) {
        case DashboardBlockType.CARTESIAN:
        case DashboardBlockType.COMPARISON_COLUMN:
        case DashboardBlockType.COMPARISON_BAR:
        case DashboardBlockType.PIE:
        case DashboardBlockType.NUMBER:
        case DashboardBlockType.SOLID_GAUGE:
            const series =
                block.TypeID === DashboardBlockType.CARTESIAN ? block.CartesianSeries :
                block.TypeID === DashboardBlockType.COMPARISON_COLUMN ? block.ComparisonColumnSeries :
                block.TypeID === DashboardBlockType.COMPARISON_BAR ? block.ComparisonBarSeries :
                block.TypeID === DashboardBlockType.PIE ? block.PieSeries :
                block.TypeID === DashboardBlockType.NUMBER ? block.NumberSeries :
                block.TypeID === DashboardBlockType.SOLID_GAUGE ? block.SolidGaugeSeries :
                [];

            if (series.length > 0) {
                const availablePeriods = series.reduce((result, serie) => {
                    let availability = null;

                    if (serie.GraphTypeID !== CartesianSerieType.STACKED && serie.GraphTypeID !== CartesianSerieType.GROUPED) {
                        availability = findIndicatorData(state.indicatorsAvailability, {
                            IndicatorID: serie.IndicatorID,
                            PeriodTypeID: null,
                            CorporateStructureLink: serie.CorporateStructureLink
                        });
                        if (!availability || availability.Custom.status !== RequestStatus.SUCCEEDED) {
                            return [];
                        }
                    }
                    else {
                        availability = {
                            ActualPeriodTypes: [],
                            BudgetPeriodTypes: [],
                            ForecastPeriodTypes: [],
                            GoalPeriodTypes: []
                        };

                        const indicator = state.indicators.Data.getByID(serie.IndicatorID);
                        if (!indicator || indicator.Custom.status !== RequestStatus.SUCCEEDED) {
                            return [];
                        }
                        const relatedIndicatorsIDs = (indicator.Formula || [])
                            .filter(({ ElementTypeID }) => FORMULA_INDICATOR_ELEMENT_TYPES.includes(ElementTypeID))
                            .map(({ TargetIndicatorID }) => TargetIndicatorID).unique() || [];

                        for (let i = 0; i < relatedIndicatorsIDs.length; i++) {
                            const subavailability = findIndicatorData(state.indicatorsAvailability, {
                                IndicatorID: relatedIndicatorsIDs[i],
                                PeriodTypeID: null,
                                CorporateStructureLink: serie.CorporateStructureLink
                            });
                            if (!subavailability || subavailability.Custom.status !== RequestStatus.SUCCEEDED) {
                                return [];
                            }

                            availability.ActualPeriodTypes.push(...subavailability.ActualPeriodTypes);
                            availability.BudgetPeriodTypes.push(...subavailability.BudgetPeriodTypes);
                            availability.ForecastPeriodTypes.push(...subavailability.ForecastPeriodTypes);
                            availability.GoalPeriodTypes.push(...subavailability.GoalPeriodTypes);
                        }
                    }

                    switch (block.TypeID !== DashboardBlockType.PIE ? serie.PastDimensionID : PerformanceIndicatorDimension.ACTUAL) {
                        case PerformanceIndicatorDimension.ACTUAL:
                            return availability.ActualPeriodTypes.filter(p => result.includes(p));
                        case PerformanceIndicatorDimension.BUDGET:
                            return availability.BudgetPeriodTypes.filter(p => result.includes(p));
                        case PerformanceIndicatorDimension.FORECAST:
                            return availability.ForecastPeriodTypes.filter(p => result.includes(p));
                        case PerformanceIndicatorDimension.GOAL:
                            return availability.GoalPeriodTypes.filter(p => result.includes(p));
                        default:
                            throw new Error(`Unknown dimension: ${serie.PastDimensionID}`);
                    }
                }, orderedPeriodTypes);
                if (availablePeriods.length) {
                    return orderedPeriodTypes.find(pt => availablePeriods.includes(pt));
                }
            }
            break;

        case DashboardBlockType.NEWSFEED:
        case DashboardBlockType.POWER_BI:
        case DashboardBlockType.CUSTOM_TABLE:
        case DashboardBlockType.STRATEGY:
        case DashboardBlockType.MILESTONE:
            break;

        case DashboardBlockType.ACTION:
        case DashboardBlockType.EXTERNAL:
        case DashboardBlockType.MARKET_OVERVIEW:
        case DashboardBlockType.POLL:
        case DashboardBlockType.TABLE:
        case DashboardBlockType.BUBBLE:
        case DashboardBlockType.GOOGLE_SHEET:
        case DashboardBlockType.CASHFLOW:
        case DashboardBlockType.SCENARIO_CASHFLOW:
            return PeriodType.MONTH;

        default:
            throw new Error(`Unknown block type: ${block.TypeID}`);
    }

    return block.DefaultPeriodTypeID || PeriodType.MONTH;
}

function block(state, action) {
    state = state || null;
    let newState = state;

    switch (action.type) {
        case ActionType.ADD_DASHBOARD_BLOCK:
        case ActionType.ADD_DASHBOARD_BLOCK_SUCCESS:
        case ActionType.ADD_DASHBOARD_BLOCK_FROM_TEMPLATE:
        case ActionType.ADD_DASHBOARD_BLOCK_FROM_TEMPLATE_SUCCESS:
        case ActionType.CLONE_DASHBOARD_BLOCK:
        case ActionType.CLONE_DASHBOARD_BLOCK_SUCCESS:
        case ActionType.EDIT_DASHBOARD_BLOCK_SUCCESS:
        case ActionType.ADD_DASHBOARD_CARTESIAN_SERIE_SUCCESS:
        case ActionType.EDIT_DASHBOARD_CARTESIAN_SERIE_SUCCESS:
        case ActionType.DELETE_DASHBOARD_CARTESIAN_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_CARTESIAN_SERIE_GROUPING_SUCCESS:
        case ActionType.EDIT_DASHBOARD_CARTESIAN_SERIE_GROUPING_SUCCESS:
        case ActionType.DELETE_DASHBOARD_CARTESIAN_SERIE_GROUPING_SUCCESS:
        case ActionType.ADD_DASHBOARD_COMPARISON_COLUMN_SERIE_SUCCESS:
        case ActionType.EDIT_DASHBOARD_COMPARISON_COLUMN_SERIE_SUCCESS:
        case ActionType.DELETE_DASHBOARD_COMPARISON_COLUMN_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_COMPARISON_BAR_SERIE_SUCCESS:
        case ActionType.EDIT_DASHBOARD_COMPARISON_BAR_SERIE_SUCCESS:
        case ActionType.DELETE_DASHBOARD_COMPARISON_BAR_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_PIE_SERIE_SUCCESS:
        case ActionType.EDIT_DASHBOARD_PIE_SERIE_SUCCESS:
        case ActionType.DELETE_DASHBOARD_PIE_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_NUMBER_SERIE_SUCCESS:
        case ActionType.EDIT_DASHBOARD_NUMBER_SERIE_SUCCESS:
        case ActionType.DELETE_DASHBOARD_NUMBER_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_SOLID_GAUGE_SERIE_SUCCESS:
        case ActionType.EDIT_DASHBOARD_SOLID_GAUGE_SERIE_SUCCESS:
        case ActionType.DELETE_DASHBOARD_SOLID_GAUGE_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_STRATEGIC_ACTION_SUCCESS:
        case ActionType.EDIT_DASHBOARD_STRATEGIC_ACTION_SUCCESS:
        case ActionType.ADD_ALL_DASHBOARD_FINANCIAL_REPORT_SERIES_SUCCESS:
        case ActionType.DELETE_ALL_DASHBOARD_FINANCIAL_REPORT_SERIES_SUCCESS:
        case ActionType.EDIT_DASHBOARD_EXTERNAL_SETTINGS_SUCCESS:
        case ActionType.ADD_DASHBOARD_BLOCK_POLL_SETTINGS_SUCCESS:
        case ActionType.EDIT_DASHBOARD_BLOCK_POLL_SETTINGS_SUCCESS:
        case ActionType.DELETE_DASHBOARD_BLOCK_POLL_SETTINGS_SUCCESS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_ROLLING_COMPARISONS_SUCCESS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_ROLLING_COMPARISONS_SUCCESS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_ROLLING_COMPARISONS_SUCCESS:
        case ActionType.ADD_DASHBOARD_TABLE_SERIE_ROW_EXPANDED_INDICATOR_SUCCESS:
        case ActionType.DELETE_DASHBOARD_TABLE_SERIE_ROW_EXPANDED_INDICATOR_SUCCESS:
        case ActionType.ADD_DASHBOARD_BLOCK_BUBBLE_SETTINGS_SUCCESS:
        case ActionType.EDIT_DASHBOARD_BLOCK_BUBBLE_SETTINGS_SUCCESS:
        case ActionType.EDIT_DASHBOARD_GOOGLE_SHEET_SETTINGS_SUCCESS:
        case ActionType.EDIT_DASHBOARD_BLOCK_POWER_BI_EMBED_SUCCESS:
        case ActionType.EDIT_DASHBOARD_BLOCK_CUSTOM_TABLE_QUERY:
        case ActionType.EDIT_DASHBOARD_BLOCK_CUSTOM_TABLE_QUERY_SUCCESS:
        case ActionType.ADD_ALL_DASHBOARD_CUSTOM_TABLE_COLUMNS_SUCCESS:
        case ActionType.EDIT_ALL_DASHBOARD_CUSTOM_TABLE_COLUMNS_SUCCESS:
        case ActionType.DELETE_ALL_DASHBOARD_CUSTOM_TABLE_COLUMNS_SUCCESS:
        case ActionType.LOAD_CUSTOM_TABLE_BLOCK_DATA_SUCCESS:
        case ActionType.EDIT_DASHBOARD_BLOCK_STRATEGY_SUCCESS:
        case ActionType.EDIT_DASHBOARD_BLOCK_MILESTONE_SUCCESS:
            return deepAssign(state, {
                Custom: {
                    revertTo: deepAssign(state, {
                        Custom: {
                            revertTo: null
                        }
                    })
                }
            });

        case ActionType.EDIT_DASHBOARD_BLOCK:
            return deepAssign(state, {
                RollingTableColumnsPastDimensionID: state.HasRollingTableColumns ? state.RollingTableColumnsPastDimensionID : null,
                RollingTableColumnsAlternativePastDimensionID: state.HasRollingTableColumns ? state.RollingTableColumnsAlternativePastDimensionID : null,
                RollingTableColumnsFutureDimensionID: state.HasRollingTableColumns ? state.RollingTableColumnsFutureDimensionID : null,
                RollingTableColumnsAlternativeFutureDimensionID: state.HasRollingTableColumns ? state.RollingTableColumnsAlternativeFutureDimensionID : null,
                RollingTableColumnsTotalEnabled: state.HasRollingTableColumns ? state.RollingTableColumnsTotalEnabled : null,
                TableRollingComparisons: state.HasRollingTableColumns ? state.TableRollingComparisons : [],
                TableDataColumns: state.HasRollingTableColumns ? [] : state.TableDataColumns,
                TableComparisonColumns: state.HasRollingTableColumns ? [] : state.TableComparisonColumns,
                TrackingLevelRowsIndicatorID: state.HasTrackingLevelRows ? state.TrackingLevelRowsIndicatorID : null,
                TrackingLevelRowsTrackingLevelID: state.HasTrackingLevelRows ? state.TrackingLevelRowsTrackingLevelID : null,
                TableSerieRows: state.HasTrackingLevelRows ? [] : state.TableSerieRows,
                TableComparisonRows: state.HasTrackingLevelRows ? [] : state.TableComparisonRows,
                TableEmptyRows: state.HasTrackingLevelRows ? [] : state.TableEmptyRows
            });

        case ActionType.ADD_ALL_DASHBOARD_TABLE_DATA_COLUMNS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_DATA_COLUMNS_PENDING:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_DATA_COLUMNS_SUCCESS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_DATA_COLUMNS_FAIL:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_DATA_COLUMNS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_DATA_COLUMNS_PENDING:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_DATA_COLUMNS_SUCCESS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_DATA_COLUMNS_FAIL:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_DATA_COLUMNS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_DATA_COLUMNS_PENDING:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_DATA_COLUMNS_SUCCESS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_DATA_COLUMNS_FAIL:
            const
                actionTableDataColumns = action.tableDataColumns,
                pinnedTableDataColumns = actionTableDataColumns?.filter(({ Sort }) => Sort).map(actionDataColumn =>
                    state.TableDataColumns.getByID(actionDataColumn.ID) ||
                            state.TableDataColumns.find(({ Custom: { internalID } }) => internalID === actionDataColumn.ID)
                        ) ||
                    [];

            let otherTableDataAndComparisonColumns =
                    [
                        ...state.TableDataColumns,
                        ...action.type !== ActionType.DELETE_ALL_DASHBOARD_TABLE_DATA_COLUMNS_SUCCESS ?
                            state.TableComparisonColumns :
                            state.TableComparisonColumns.filter(comparisonColumn =>
                                !action.tableDataColumnIDs.some(dataColumnID =>
                                    dataColumnID === comparisonColumn.FromColumnID || dataColumnID === comparisonColumn.ToColumnID
                                )
                            ),
                        ...action.type !== ActionType.DELETE_ALL_DASHBOARD_TABLE_DATA_COLUMNS_SUCCESS ?
                            state.TableSumColumns :
                            state.TableSumColumns.filter(sumColumn =>
                                !action.tableDataColumnIDs.some(dataColumnID =>
                                    dataColumnID === sumColumn.FromColumnID || dataColumnID === sumColumn.ToColumnID
                                )
                            )

                    ]
                        .filter(column => !pinnedTableDataColumns.includes(column))
                        .sortBy(({ Sort }) => Sort);

            pinnedTableDataColumns.forEach(pinnedDataColumn => otherTableDataAndComparisonColumns.splice(pinnedDataColumn.Sort - 1, 0, pinnedDataColumn));

            otherTableDataAndComparisonColumns = otherTableDataAndComparisonColumns.map((column, index) => ({ ...column, Sort: index + 1 }));

            return deepAssign(state, {
                TableDataColumns: otherTableDataAndComparisonColumns.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableDataColumn.EntityType),
                TableComparisonColumns: otherTableDataAndComparisonColumns.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableComparisonColumn.EntityType),
                TableSumColumns: otherTableDataAndComparisonColumns.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableSumColumn.EntityType),
                Custom: {
                    revertTo: deepAssign(state, {
                        Custom: {
                            revertTo: null
                        }
                    })
                }
            });

        case ActionType.ADD_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_PENDING:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_SUCCESS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_FAIL:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_PENDING:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_SUCCESS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_FAIL:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_PENDING:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_SUCCESS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_FAIL:
            const
                actionTableComparisonColumns = action.tableComparisonColumns,
                pinnedTableComparisonColumns = actionTableComparisonColumns?.filter(({ Sort }) => Sort).map(actionComparisonColumn =>
                    state.TableComparisonColumns.getByID(actionComparisonColumn.ID) ||
                            state.TableComparisonColumns.find(({ Custom: { internalID } }) => internalID === actionComparisonColumn.ID)
                        ) ||
                    [];

            let otherTableComparisonAndDataColumns = [...state.TableComparisonColumns, ...state.TableDataColumns, ...state.TableSumColumns]
                .filter(column => !pinnedTableComparisonColumns.includes(column))
                .sortBy(({ Sort }) => Sort);

            pinnedTableComparisonColumns.forEach(pinnedComparisonColumn => otherTableComparisonAndDataColumns.splice(pinnedComparisonColumn.Sort - 1, 0, pinnedComparisonColumn));

            otherTableComparisonAndDataColumns = otherTableComparisonAndDataColumns.map((column, index) => ({ ...column, Sort: index + 1 }));

            return deepAssign(state, {
                TableComparisonColumns: otherTableComparisonAndDataColumns.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableComparisonColumn.EntityType),
                TableDataColumns: otherTableComparisonAndDataColumns.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableDataColumn.EntityType),
                TableSumColumns: otherTableComparisonAndDataColumns.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableSumColumn.EntityType),
                Custom: {
                    revertTo: deepAssign(state, {
                        Custom: {
                            revertTo: null
                        }
                    })
                }
            });

        case ActionType.ADD_ALL_DASHBOARD_TABLE_SUM_COLUMNS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_SUM_COLUMNS_PENDING:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_SUM_COLUMNS_SUCCESS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_SUM_COLUMNS_FAIL:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_SUM_COLUMNS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_SUM_COLUMNS_PENDING:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_SUM_COLUMNS_SUCCESS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_SUM_COLUMNS_FAIL:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_SUM_COLUMNS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_SUM_COLUMNS_PENDING:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_SUM_COLUMNS_SUCCESS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_SUM_COLUMNS_FAIL:
            const
                actionTableSumColumns = action.tableSumColumns,
                pinnedTableSumColumns = actionTableSumColumns?.filter(({ Sort }) => Sort).map(actionSumColumn =>
                    state.TableSumColumns.getByID(actionSumColumn.ID) ||
                                state.TableSumColumns.find(({ Custom: { internalID } }) => internalID === actionSumColumn.ID)
                            ) ||
                        [];

            let otherTableSumAndDataColumns = [...state.TableSumColumns, ...state.TableDataColumns, ...state.TableComparisonColumns]
                .filter(column => !pinnedTableSumColumns.includes(column))
                .sortBy(({ Sort }) => Sort);

            pinnedTableSumColumns.forEach(pinnedSumColumn => otherTableSumAndDataColumns.splice(pinnedSumColumn.Sort - 1, 0, pinnedSumColumn));

            otherTableSumAndDataColumns = otherTableSumAndDataColumns.map((column, index) => ({ ...column, Sort: index + 1 }));

            return deepAssign(state, {
                TableComparisonColumns: otherTableSumAndDataColumns.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableComparisonColumn.EntityType),
                TableDataColumns: otherTableSumAndDataColumns.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableDataColumn.EntityType),
                TableSumColumns: otherTableSumAndDataColumns.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableSumColumn.EntityType),
                Custom: {
                    revertTo: deepAssign(state, {
                        Custom: {
                            revertTo: null
                        }
                    })
                }
            });

        case ActionType.ADD_ALL_DASHBOARD_TABLE_SERIE_ROWS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_SERIE_ROWS_PENDING:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_SERIE_ROWS_SUCCESS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_SERIE_ROWS_FAIL:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_SERIE_ROWS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_SERIE_ROWS_PENDING:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_SERIE_ROWS_SUCCESS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_SERIE_ROWS_FAIL:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_SERIE_ROWS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_SERIE_ROWS_PENDING:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_SERIE_ROWS_SUCCESS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_SERIE_ROWS_FAIL:
            const pinnedTableSerieRows = (action.tableSerieRows || [])
                .filter(({ Sort }) => Sort)
                .map(({ ID }) => state.TableSerieRows.getByID(ID) || state.TableSerieRows.find(({ Custom: { internalID } }) => internalID === ID))
                .filter(v => v);

            let otherTableSerieAndComparisonAndEmptyRows =
                    [
                        ...state.TableSerieRows,
                        ...action.type !== ActionType.DELETE_ALL_DASHBOARD_TABLE_SERIE_ROWS_SUCCESS ?
                            state.TableComparisonRows :
                            state.TableComparisonRows.filter(comparisonRow =>
                                !action.tableSerieRowIDs.some(serieRowID =>
                                    serieRowID === comparisonRow.FromRowID || serieRowID === comparisonRow.ToRowID
                                )
                            ),
                        ...state.TableEmptyRows
                    ]
                        .filter(row => !pinnedTableSerieRows.some(({ ID, Custom }) => ID === row.ID || Custom.internalID === row.ID))
                        .sortBy(({ Sort }) => Sort);

            pinnedTableSerieRows.forEach(pinnedSerieRow => otherTableSerieAndComparisonAndEmptyRows.splice(pinnedSerieRow.Sort - 1, 0, pinnedSerieRow));

            otherTableSerieAndComparisonAndEmptyRows = otherTableSerieAndComparisonAndEmptyRows.map((row, index) => ({ ...row, Sort: index + 1 }));

            newState = deepAssign(state, {
                TableSerieRows: otherTableSerieAndComparisonAndEmptyRows.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableSerieRowReport.EntityType),
                TableComparisonRows: otherTableSerieAndComparisonAndEmptyRows.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableComparisonRow.EntityType),
                TableEmptyRows: otherTableSerieAndComparisonAndEmptyRows.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableEmptyRow.EntityType),
                Custom: {
                    revertTo: deepAssign(state, {
                        Custom: {
                            revertTo: null
                        }
                    })
                }
            });

            if (action.type.endsWith('_SUCCESS')) {
                return deepAssign(newState, {
                    Custom: {
                        revertTo: {
                            TableSerieRows: newState.TableSerieRows,
                            TableComparisonRows: newState.TableComparisonRows,
                            TableEmptyRows: newState.TableEmptyRows
                        }
                    }
                });
            }

            return newState;

        case ActionType.ADD_ALL_DASHBOARD_TABLE_COMPARISON_ROWS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_PENDING:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_SUCCESS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_FAIL:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_COMPARISON_ROWS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_PENDING:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_SUCCESS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_FAIL:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_ROWS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_PENDING:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_SUCCESS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_FAIL:
            const pinnedTableComparisonRows = (action.tableComparisonRows || [])
                .filter(({ Sort }) => Sort)
                .map(({ ID }) => state.TableComparisonRows.getByID(ID) || state.TableComparisonRows.find(({ Custom: { internalID } }) => internalID === ID))
                .filter(v => v);

            let otherTableComparisonAndSerieAndEmptyRows =
                    [
                        ...state.TableSerieRows,
                        ...state.TableComparisonRows,
                        ...state.TableEmptyRows
                    ]
                        .filter(row => !pinnedTableComparisonRows.some(({ ID, Custom }) => ID === row.ID || Custom.internalID === row.ID))
                        .sortBy(({ Sort }) => Sort);

            pinnedTableComparisonRows.forEach(pinnedComparisonRow => otherTableComparisonAndSerieAndEmptyRows.splice(pinnedComparisonRow.Sort - 1, 0, pinnedComparisonRow));

            otherTableComparisonAndSerieAndEmptyRows = otherTableComparisonAndSerieAndEmptyRows.map((row, index) => ({ ...row, Sort: index + 1 }));

            newState = deepAssign(state, {
                TableSerieRows: otherTableComparisonAndSerieAndEmptyRows.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableSerieRowReport.EntityType),
                TableComparisonRows: otherTableComparisonAndSerieAndEmptyRows.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableComparisonRow.EntityType),
                TableEmptyRows: otherTableComparisonAndSerieAndEmptyRows.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableEmptyRow.EntityType),
                Custom: {
                    revertTo: deepAssign(state, {
                        Custom: {
                            revertTo: null
                        }
                    })
                }
            });

            if (action.type.endsWith('_SUCCESS')) {
                return deepAssign(newState, {
                    Custom: {
                        revertTo: {
                            TableSerieRows: newState.TableSerieRows,
                            TableComparisonRows: newState.TableComparisonRows,
                            TableEmptyRows: newState.TableEmptyRows
                        }
                    }
                });
            }

            return newState;

        case ActionType.ADD_ALL_DASHBOARD_TABLE_EMPTY_ROWS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_EMPTY_ROWS_PENDING:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_EMPTY_ROWS_SUCCESS:
        case ActionType.ADD_ALL_DASHBOARD_TABLE_EMPTY_ROWS_FAIL:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_EMPTY_ROWS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_EMPTY_ROWS_PENDING:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_EMPTY_ROWS_SUCCESS:
        case ActionType.EDIT_ALL_DASHBOARD_TABLE_EMPTY_ROWS_FAIL:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_EMPTY_ROWS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_EMPTY_ROWS_PENDING:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_EMPTY_ROWS_SUCCESS:
        case ActionType.DELETE_ALL_DASHBOARD_TABLE_EMPTY_ROWS_FAIL:
            const pinnedTableEmptyRows = (action.tableEmptyRows || [])
                .filter(({ Sort }) => Sort)
                .map(({ ID }) => state.TableEmptyRows.getByID(ID) || state.TableEmptyRows.find(({ Custom: { internalID } }) => internalID === ID))
                .filter(v => v);

            let otherTableEmptyAndSerieAndComparisonRows =
                    [
                        ...state.TableSerieRows,
                        ...state.TableComparisonRows,
                        ...state.TableEmptyRows
                    ]
                        .filter(row => !pinnedTableEmptyRows.some(({ ID, Custom }) => ID === row.ID || Custom.internalID === row.ID))
                        .sortBy(({ Sort }) => Sort);

            pinnedTableEmptyRows.forEach(pinnedEmptyRow => otherTableEmptyAndSerieAndComparisonRows.splice(pinnedEmptyRow.Sort - 1, 0, pinnedEmptyRow));

            otherTableEmptyAndSerieAndComparisonRows = otherTableEmptyAndSerieAndComparisonRows.map((row, index) => ({ ...row, Sort: index + 1 }));

            newState = deepAssign(state, {
                TableSerieRows: otherTableEmptyAndSerieAndComparisonRows.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableSerieRowReport.EntityType),
                TableComparisonRows: otherTableEmptyAndSerieAndComparisonRows.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableComparisonRow.EntityType),
                TableEmptyRows: otherTableEmptyAndSerieAndComparisonRows.filter(({ EntityType }) => EntityType === defaults.DashboardPageBlockTableEmptyRow.EntityType),
                Custom: {
                    revertTo: deepAssign(state, {
                        Custom: {
                            revertTo: null
                        }
                    })
                }
            });

            if (action.type.endsWith('_SUCCESS')) {
                return deepAssign(newState, {
                    Custom: {
                        revertTo: {
                            TableSerieRows: newState.TableSerieRows,
                            TableComparisonRows: newState.TableComparisonRows,
                            TableEmptyRows: newState.TableEmptyRows
                        }
                    }
                });
            }

            return newState;

        default:
            return state;
    }
}

function pages(state, action) {
    state = state || defaults.NetworkArrayOf(defaults.DashboardPageReport);

    switch (action.type) {
        case ActionType.CLONE_DASHBOARD_PAGE_PENDING:
            if (action.targetCompanyID) {
                return state;
            }

            let newPageID = action.changes && (action.changes.ID || action.changes.Custom && action.changes.Custom.internalID) || defaults.DashboardPageReport.ID;
            if (newPageID === action.sourcePageID) {
                newPageID = defaults.DashboardPageReport.ID;
            }
            return deepAssign(state, {
                Data: {
                    [state.Data.length]: deepAssign(state.Data.getByID(action.sourcePageID), {
                        ...action.changes,
                        ID: newPageID,
                        Custom: {
                            internalID: newPageID,
                            sourcePageID: action.sourcePageID,
                            targetCompanyID: action.targetCompanyID
                        }
                    })
                }
            });

        case ActionType.CLONE_DASHBOARD_BLOCK_PENDING:
            if (action.targetCompanyID) {
                return state;
            }

            const
                { sourcePageID } = action,
                targetPageID = action.changes && action.changes.PageID || sourcePageID;

            let newBlockID = action.changes && (action.changes.ID || action.changes.Custom && action.changes.Custom.internalID) || defaults.DashboardPageBlockReport.ID;
            if (newBlockID === action.sourceBlockID) {
                newBlockID = defaults.DashboardPageBlockReport.ID;
            }
            return deepAssign(state, {
                Data: {
                    [state.Data.getIndexByID(targetPageID)]: {
                        Blocks: {
                            Data: {
                                [state.Data.getByID(targetPageID).Blocks.Data.length]: deepAssign(state.Data.getByID(sourcePageID).Blocks.Data.getByID(action.sourceBlockID), {
                                    ...action.changes,
                                    ID: newBlockID,
                                    GridXCellStart: 0,
                                    GridYCellStart: state.Data.getByID(targetPageID).Blocks.Data.map(block => block.GridYCellStart + block.HeightGridCells).maxBy() || 0,
                                    Custom: {
                                        internalID: newBlockID,
                                        sourcePageID: action.sourcePageID,
                                        sourceBlockID: action.sourceBlockID,
                                        targetCompanyID: action.targetCompanyID
                                    }
                                })
                            }
                        },
                        Updated: new Date()
                    }
                }
            });

        case ActionType.DELETE_PERFORMANCE_INDICATOR_SUCCESS:
            return defaults.NetworkArrayOf(defaults.DashboardPageReport);

        case ActionType.LOAD_ALL_DASHBOARD_PAGES_SUCCESS:
            return deepAssign(state, {
                Data: state.Data.map(page => deepAssign(page, {
                    Blocks: {
                        Data: page.Blocks.Data.map(block => ({
                            Custom: {
                                revertTo: deepAssign(block, {
                                    Custom: {
                                        revertTo: null
                                    }
                                })
                            }
                        }))
                    },
                    NewsEntries: page.NewsEntries || defaults.NewsEntryLightReportPaginationSlice,
                    UserAccesses: page.UserAccesses || defaults.NetworkArray
                }))
            });

        case ActionType.ADD_DASHBOARD_BLOCK_SUCCESS:
        case ActionType.ADD_DASHBOARD_BLOCK_FROM_TEMPLATE_SUCCESS:
        case ActionType.DELETE_DASHBOARD_BLOCK_SUCCESS:
            const indexOfPage = state.Data.getIndexByID(action.pageID);

            return state.Data[indexOfPage] ?
                deepAssign(state, {
                    Data: {
                        [indexOfPage]: {
                            Updated: new Date()
                        }
                    }
                }) :
                state;

        case ActionType.REVERT_DASHBOARD_BLOCK:
            if (action.blockID) {
                const pageIndex = state.Data.getIndexByID(action.pageID),
                    page = state.Data[pageIndex];
                if (!page) {
                    return state;
                }

                const blockIndex = page.Blocks.Data.getIndexByID(action.blockID),
                    block = page.Blocks[blockIndex];
                if (!block) {
                    return state;
                }

                state = deepAssign(state, {
                    Data: {
                        [pageIndex]: {
                            Blocks: {
                                Data: {
                                    [blockIndex]: deepAssign(block.Custom.revertTo, {
                                        Comments: block.Comments,
                                        Custom: { revertTo: block.Custom.revertTo },
                                        Newsfeed: block.Newsfeed
                                    })
                                }
                            }
                        }
                    }
                });
            }

            return state;

        case ActionType.MOVE_DASHBOARD_BLOCK_TO_ANOTHER_PAGE_PENDING:
            const pageIndex = state.Data.getIndexByID(action.pageID);
            const page = state.Data[pageIndex];
            if (!page) {
                return state;
            }

            return deepAssign(state, {
                Data: {
                    [pageIndex]: {
                        Blocks: {
                            Data: page.Blocks.Data.map(block => block.ID === action.blockID ? { ...block, Custom: { status: RequestStatus.PENDING } } : { ...block })
                        }
                    }
                }
            });

        case ActionType.MOVE_DASHBOARD_BLOCK_TO_ANOTHER_PAGE_SUCCESS:
            const index = state.Data.getIndexByID(action.pageID);
            const oldPage = state.Data[index];
            if (!oldPage) {
                return state;
            }

            return deepAssign(state, {
                Data: {
                    [index]: {
                        Blocks: {
                            Count: oldPage.Blocks.Count - 1,
                            TotalCount: oldPage.Blocks.TotalCount - 1,
                            Data: oldPage.Blocks.Data.filter(block => block.ID !== action.blockID)
                        }
                    }
                }
            });

        case 'EDIT_TRACKING_LEVEL':
            const pindex = action.payload.entity.DefaultDashboardPageID ? state.Data.getIndexByID(action.payload.entity.DefaultDashboardPageID) : -1;
            return pindex !== -1 ?
                deepAssign(state, {
                    Data: {
                        [pindex]: { [CORPORATE_STRUCTURE_DASHBOARD_FILTERING_ENABLEDNESS_PROPERTIES_MAP[action.payload.entityID]]: true }
                    }
                }) :
                state;

        case ActionType.EDIT_MEETING_STATUS_SUCCESS:
            return action.statusID === MeetingStatus.ARCHIVED ?
                deepAssign(state, { Custom: { status: RequestStatus.UNSYNCED } }) :
                state;

        case ActionType.STORE_DASHBOARD_PAGE_SCREENSHOT_SUCCESS:
            return deepAssign(state, {
                Data: {
                    [state.Data.getIndexByID(action.pageID)]: {
                        PageScreenshotImageURI: action.response,
                        PageScreenshotImageTaken: new Date()
                    }
                }
            });

        case ActionType.INIT_DASHBOARD_PAGE_BLOCK_IMAGES:
            if (action.pageID) {
                const
                    pageIndex = state.Data.getIndexByID(action.pageID),
                    page = state.Data[pageIndex],
                    chartTypes = action.supportedBlockTypes || [];

                if (!page) {
                    return state;
                }

                return deepAssign(state, {
                    Data: {
                        [pageIndex]: {
                            Blocks: {
                                Data: page.Blocks.Data.map(block => chartTypes.includes(block.TypeID) ? {
                                    ...block,
                                    Custom: {
                                        blockImage: {
                                            ...defaults.DashboardPageBlockImage,
                                            BlockID: block.ID
                                        }
                                    }
                                } : {
                                    ...block,
                                    Custom: { blockImage: null }
                                })
                            }
                        }
                    }
                });
            }

            return state;

        case ActionType.EDIT_DASHBOARD_BLOCK_IMAGE:
            if (action.pageID && action.blockID) {
                const
                    pageIndex = state.Data.getIndexByID(action.pageID),
                    page = state.Data[pageIndex];

                if (!page) {
                    return state;
                }

                return deepAssign(state, {
                    Data: {
                        [pageIndex]: {
                            Blocks: {
                                Data: page.Blocks.Data.map(block => block.ID === action.blockID ? {
                                    ...block,
                                    Custom: { blockImage: action.blockImage }
                                } : { ...block })
                            }
                        }
                    }
                });
            }

            return state;

        case ActionType.GENERATE_BUSINESS_REVIEW_PDF_PENDING:
        case ActionType.ADD_BUSINESS_REVIEW_PENDING:
            if (action.pageID) {
                const
                    pageIndex = state.Data.getIndexByID(action.pageID),
                    page = state.Data[pageIndex];

                if (!page) {
                    return state;
                }

                return deepAssign(state, {
                    Data: {
                        [pageIndex]: {
                            Custom: { isBusinessReviewPending: true }
                        }
                    }
                });
            }

            return state;

        case ActionType.GENERATE_BUSINESS_REVIEW_PDF_SUCCESS:
        case ActionType.GENERATE_BUSINESS_REVIEW_PDF_FAIL:
        case ActionType.ADD_BUSINESS_REVIEW_SUCCESS:
        case ActionType.ADD_BUSINESS_REVIEW_FAIL:
            if (action.pageID) {
                const
                    pageIndex = state.Data.getIndexByID(action.pageID),
                    page = state.Data[pageIndex];

                if (!page) {
                    return state;
                }

                return deepAssign(state, {
                    Data: {
                        [pageIndex]: {
                            Blocks: {
                                Data: page.Blocks.Data.map(block => ({ ...block, Custom: { blockImage: undefined } }))
                            },
                            Custom: { isBusinessReviewPending: false }
                        }
                    }
                });
            }

            return state;

        case 'SET_STRATEGY_CONTENT_SETTINGS':
            const { dashboardPageID, dashboardBlockID } = action.payload;

            if (dashboardPageID && dashboardBlockID) {
                const
                    pageIndex = state.Data.getIndexByID(dashboardPageID),
                    page = state.Data[pageIndex];

                if (!page) {
                    return state;
                }

                return deepAssign(state, {
                    Data: {
                        [pageIndex]: {
                            Blocks: {
                                Data: page.Blocks.Data.map(block => block.ID === dashboardBlockID && !!block.StrategySettings ?
                                    {
                                        ...block,
                                        StrategySettings: {
                                            Custom: { strategyContentSettings: action.payload.settings }
                                        }
                                    } :
                                    { ...block }
                                )
                            }
                        }
                    }
                });
            }
            else {
                return state;
            }

        default:
            return state;
    }
}

function dashboard(state, action) {
    state = state || {};

    if (action.type === ActionType.EDIT_DASHBOARD_BLOCK) {
        const page = state.pages.Data.getByID(action.pageID),
            block = page?.Blocks.Data.getByID(action.blockID);
        if (!block || block.PeriodTypeID === action.block.PeriodTypeID && block.RangeStartYearOffset === action.block.RangeStartYearOffset && block.RangeStartPeriodOffset === action.block.RangeStartPeriodOffset && block.Range === action.block.Range) {
            return state;
        }
    }

    switch (action.type) {
        // TODO decide what is the best caching strategy
        // currently it is OK to store everything until we get signal of data change from server
        // so used data won't be loaded next time user wants to see it and transitions would be faster
        // but wouldn't it lead to enormous memory consumption when system gets bigger?

        case ActionType.EDIT_DASHBOARD_BLOCK:
        case ActionType.ADD_DASHBOARD_CARTESIAN_SERIE:
        case ActionType.EDIT_DASHBOARD_CARTESIAN_SERIE:
        case ActionType.DELETE_DASHBOARD_CARTESIAN_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_CARTESIAN_SERIE_GROUPING:
        case ActionType.EDIT_DASHBOARD_CARTESIAN_SERIE_GROUPING:
        case ActionType.DELETE_DASHBOARD_CARTESIAN_SERIE_GROUPING_SUCCESS:
        case ActionType.ADD_DASHBOARD_COMPARISON_COLUMN_SERIE:
        case ActionType.EDIT_DASHBOARD_COMPARISON_COLUMN_SERIE:
        case ActionType.DELETE_DASHBOARD_COMPARISON_COLUMN_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_COMPARISON_BAR_SERIE:
        case ActionType.EDIT_DASHBOARD_COMPARISON_BAR_SERIE:
        case ActionType.DELETE_DASHBOARD_COMPARISON_BAR_SERIE_SUCCESS:
        case ActionType.SEARCH_DASHBOARD_INDICATOR_AVAILABILITY_SUCCESS:
        case ActionType.ADD_DASHBOARD_PIE_SERIE:
        case ActionType.EDIT_DASHBOARD_PIE_SERIE:
        case ActionType.DELETE_DASHBOARD_PIE_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_NUMBER_SERIE:
        case ActionType.EDIT_DASHBOARD_NUMBER_SERIE:
        case ActionType.DELETE_DASHBOARD_NUMBER_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_SOLID_GAUGE_SERIE:
        case ActionType.EDIT_DASHBOARD_SOLID_GAUGE_SERIE:
        case ActionType.DELETE_DASHBOARD_SOLID_GAUGE_SERIE_SUCCESS:
        case ActionType.ADD_DASHBOARD_STRATEGIC_ACTION_SUCCESS:
        case ActionType.EDIT_DASHBOARD_STRATEGIC_ACTION_SUCCESS:

            return deepAssign(state, {
                pages: {
                    Data: state.pages.Data && state.pages.Data.map(page => deepAssign(page, {
                        Blocks: {
                            Data: page.Blocks.Data.map(block => deepAssign(block, {
                                DefaultPeriodTypeID: calculateDefaultPeriodTypeID(state, action.blockID === block.ID ? action.block || block : block),
                                Custom: {
                                    periodType: null,
                                    currentDate: null
                                }
                            }))
                        }
                    }))
                }
            });

        default:
            return state;
    }
}

const
    blockReducer = pipeReducers(
        networkEntityLoadReducer(NetworkActionPrefix.LOAD_DASHBOARD_BLOCK_PREFIX),
        combineReducers({
            CartesianSeries: combineReducers({
                '@cartesianSerieIndex': pipeReducers(
                    networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_CARTESIAN_SERIE_PREFIX, null, (state, action) => action.cartesianSerie),
                    networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_CARTESIAN_SERIE_PREFIX, null, (state, action) => action.cartesianSerie),
                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_CARTESIAN_SERIE_PREFIX, null),
                    combineReducers({
                        Groupings: combineReducers({
                            '@cartesianSerieGroupingIndex': pipeReducers(
                                networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_CARTESIAN_SERIE_GROUPING_PREFIX, null, (state, action) => action.cartesianSerieGrouping),
                                networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_CARTESIAN_SERIE_GROUPING_PREFIX, null, (state, action) => action.cartesianSerieGrouping),
                                networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_CARTESIAN_SERIE_GROUPING_PREFIX, null)
                            )
                        }, keyVariables)
                    }, keyVariables)
                )
            }, keyVariables),
            ComparisonColumnSeries: combineReducers({
                '@comparisonColumnSerieIndex': pipeReducers(
                    networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_COMPARISON_COLUMN_SERIE_PREFIX, null, (state, action) => action.comparisonColumnSerie),
                    networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_COMPARISON_COLUMN_SERIE_PREFIX, null, (state, action) => action.comparisonColumnSerie),
                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_COMPARISON_COLUMN_SERIE_PREFIX, null)
                )
            }, keyVariables),
            ComparisonBarSeries: combineReducers({
                '@comparisonBarSerieIndex': pipeReducers(
                    networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_COMPARISON_BAR_SERIE_PREFIX, null, (state, action) => action.comparisonBarSerie),
                    networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_COMPARISON_BAR_SERIE_PREFIX, null, (state, action) => action.comparisonBarSerie),
                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_COMPARISON_BAR_SERIE_PREFIX, null)
                )
            }, keyVariables),
            Newsfeed: networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_NEWSFEED_PREFIX, null, (state, action) => action.newsfeed),
            PieSeries: combineReducers({
                '@pieSerieIndex': pipeReducers(
                    networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_PIE_SERIE_PREFIX, null, (state, action) => action.pieSerie),
                    networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_PIE_SERIE_PREFIX, null, (state, action) => action.pieSerie),
                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_PIE_SERIE_PREFIX, null)
                )
            }, keyVariables),
            NumberSeries: combineReducers({
                '@numberSerieIndex': pipeReducers(
                    networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_NUMBER_SERIE_PREFIX, null, (state, action) => action.numberSerie),
                    networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_NUMBER_SERIE_PREFIX, null, (state, action) => action.numberSerie),
                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_NUMBER_SERIE_PREFIX, null)
                )
            }, keyVariables),
            SolidGaugeSeries: combineReducers({
                '@solidGaugeSerieIndex': pipeReducers(
                    networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_SOLID_GAUGE_SERIE_PREFIX, null, (state, action) => action.solidGaugeSerie),
                    networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_SOLID_GAUGE_SERIE_PREFIX, null, (state, action) => action.solidGaugeSerie),
                    networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_SOLID_GAUGE_SERIE_PREFIX, null)
                )
            }, keyVariables),
            StrategicAction: pipeReducers(
                networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_STRATEGIC_ACTION_PREFIX, null, (state, action) => action.strategicAction),
                networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_STRATEGIC_ACTION_PREFIX, null, (state, action) => action.strategicAction),
                networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_STRATEGIC_ACTION_PREFIX, null)
            ),
            ExternalSettings: networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_EXTERNAL_SETTINGS_PREFIX, null, (state, action) => action.externalSettings),
            PollSettings: pipeReducers(
                networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_BLOCK_POLL_SETTINGS_PREFIX, null, (state, action) => action.indicatorPollSettings),
                networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_BLOCK_POLL_SETTINGS_PREFIX, null, (state, action) => action.indicatorPollSettings),
                networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_BLOCK_POLL_SETTINGS_PREFIX, null)
            ),
            TableDataColumns: pipeReducers(
                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DASHBOARD_TABLE_DATA_COLUMNS_PREFIX, null, (state, action) => action.tableDataColumns),
                networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_DASHBOARD_TABLE_DATA_COLUMNS_PREFIX, null, (state, action) => action.tableDataColumns),
                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DASHBOARD_TABLE_DATA_COLUMNS_PREFIX, null, (state, action) => action.tableDataColumnIDs)
            ),
            TableComparisonColumns: pipeReducers(
                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_PREFIX, null, (state, action) => action.tableComparisonColumns),
                networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_PREFIX, null, (state, action) => action.tableComparisonColumns),
                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_COLUMNS_PREFIX, null, (state, action) => action.tableComparisonColumnIDs)
            ),
            TableSumColumns: pipeReducers(
                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DASHBOARD_TABLE_SUM_COLUMNS_PREFIX, null, (state, action) => action.tableSumColumns),
                networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_DASHBOARD_TABLE_SUM_COLUMNS_PREFIX, null, (state, action) => action.tableSumColumns),
                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DASHBOARD_TABLE_SUM_COLUMNS_PREFIX, null, (state, action) => action.tableSumColumnIDs)
            ),
            TableRollingComparisons: pipeReducers(
                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DASHBOARD_TABLE_ROLLING_COMPARISONS_PREFIX, null, (state, action) => action.tableRollingComparisons),
                networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_DASHBOARD_TABLE_ROLLING_COMPARISONS_PREFIX, null, (state, action) => action.tableRollingComparisons),
                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DASHBOARD_TABLE_ROLLING_COMPARISONS_PREFIX, null, (state, action) => action.tableRollingComparisonIDs)
            ),
            TableSerieRows: pipeReducers(
                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DASHBOARD_TABLE_SERIE_ROWS_PREFIX, null, (state, action) => action.tableSerieRows),
                networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_DASHBOARD_TABLE_SERIE_ROWS_PREFIX, null, (state, action) => action.tableSerieRows),
                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DASHBOARD_TABLE_SERIE_ROWS_PREFIX, null, (state, action) => action.tableSerieRowIDs),
                combineReducers({
                    '@tableSerieRowIndex': combineReducers({
                        ExpandedIndicators: combineReducers({
                            '@serieRowExpandedIndicatorIndex': pipeReducers(
                                networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_TABLE_SERIE_ROW_EXPANDED_INDICATOR_PREFIX, null, (state, action) => action.serieRowExpandedIndicator),
                                networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_TABLE_SERIE_ROW_EXPANDED_INDICATOR_PREFIX)
                            )
                        }, keyVariables)
                    }, keyVariables)
                }, keyVariables)
            ),
            TableComparisonRows: pipeReducers(
                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_PREFIX, null, (state, action) => action.tableComparisonRows),
                networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_PREFIX, null, (state, action) => action.tableComparisonRows),
                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_PREFIX, null, (state, action) => action.tableComparisonRowIDs),
                (state, action) => {
                    if (action.type === ActionType.DELETE_ALL_DASHBOARD_TABLE_COMPARISON_ROWS_SUCCESS) {
                        return state.filter(({ FromSumRowID, ToSumRowID }) =>
                            !action.tableComparisonRowIDs.includes(FromSumRowID) &&
                        !action.tableComparisonRowIDs.includes(ToSumRowID)
                        );
                    }

                    return state;
                }
            ),
            TableEmptyRows: pipeReducers(
                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DASHBOARD_TABLE_EMPTY_ROWS_PREFIX, null, (state, action) => action.tableEmptyRows),
                networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_DASHBOARD_TABLE_EMPTY_ROWS_PREFIX, null, (state, action) => action.tableEmptyRows),
                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DASHBOARD_TABLE_EMPTY_ROWS_PREFIX, null, (state, action) => action.tableEmptyRowIDs)
            ),
            BubbleSettings: pipeReducers(
                networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_BLOCK_BUBBLE_SETTINGS_PREFIX, null, (state, action) => action.bubbleSettings),
                networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_BLOCK_BUBBLE_SETTINGS_PREFIX, null, (state, action) => action.bubbleSettings)
            ),
            GoogleSheetSettings: networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_GOOGLE_SHEET_SETTINGS_PREFIX, null, (state, action) => action.googleSheetSettings),
            PowerBIEmbed: pipeReducers(
                networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_BLOCK_POWER_BI_EMBED_PREFIX, null, (state, action) => action.powerBIEmbed),
                combineReducers({
                    Custom: combineReducers({
                        embedToken: pipeReducers(
                            networkEntityLoadReducer(NetworkActionPrefix.GENERATE_POWER_BI_EMBED_TOKEN_PREFIX),
                            (state, action) => {
                                if (action.type === ActionType.EDIT_DASHBOARD_BLOCK_POWER_BI_EMBED_SUCCESS) {
                                    return defaults.DashboardPageBlockPowerBIEmbed.Custom.embedToken;
                                }

                                return state;
                            }
                        )
                    })
                })
            ),
            CustomTableQuery: networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_BLOCK_CUSTOM_TABLE_QUERY_PREFIX, null, (state, action) => action.customTableQuery),
            CustomTableColumns: sortableEntitiesLifecycleReducer('BlockID', pipeReducers(
                networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DASHBOARD_CUSTOM_TABLE_COLUMNS_PREFIX, null, (state, action) => action.customTableColumns),
                networkEntityEditAllReducer(NetworkActionPrefix.EDIT_ALL_DASHBOARD_CUSTOM_TABLE_COLUMNS_PREFIX, null, (state, action) => action.customTableColumns),
                networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DASHBOARD_CUSTOM_TABLE_COLUMNS_PREFIX, null, (state, action) => action.customTableColumnIDs)
            ), keyVariables),
            CustomTableData: pipeReducers(
                networkObjectLoadReducer(NetworkActionPrefix.LOAD_CUSTOM_TABLE_BLOCK_DATA_PREFIX, defaults.NetworkObjectOf('')),
                (state, action) => {
                    if (action.type === ActionType.LOAD_CUSTOM_TABLE_BLOCK_DATA_SUCCESS &&
                    (!action.response || Object.prototype.hasOwnProperty.call(JSON.parse(action.response), 'error'))) {
                        return deepAssign(state, {
                            Custom: {
                                status: RequestStatus.FAILED
                            }
                        });
                    }

                    if (action.type === ActionType.EDIT_DASHBOARD_BLOCK_CUSTOM_TABLE_QUERY_SUCCESS) {
                        return deepAssign(state, {
                            Custom: {
                                status: RequestStatus.UNSYNCED
                            }
                        });
                    }

                    return state;
                }
            ),
            StrategySettings: networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_BLOCK_STRATEGY_PREFIX, null, (state, action) => action.strategySettings),
            MilestoneSettings: networkEntityEditReducer(
                NetworkActionPrefix.EDIT_DASHBOARD_BLOCK_MILESTONE_PREFIX,
                null,
                (state, action) => action.milestoneSettings
            ),
            Comments: pipeReducers(
                networkPaginationLoadReducer(NetworkActionPrefix.LOAD_DASHBOARD_COMMENTS_PREFIX, defaults.DashboardPageBlockConversationPaginationSlice),
                paginationSlicePushReducer(ActionType.ADD_DASHBOARD_COMMENT, defaults.DashboardPageBlockConversationPaginationSlice),
                combineReducers({
                    Data: combineReducers({
                        '@commentIndex': networkEntityAddReducer(
                            NetworkActionPrefix.ADD_DASHBOARD_COMMENT_PREFIX,
                            defaults.DashboardPageBlockConversation,
                            (state, action) => action.comment)
                    }, keyVariables)
                }, keyVariables)
            )
        }, keyVariables),
        networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_BLOCK_PREFIX, null, (state, action) => action.block),
        networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_BLOCK_FROM_TEMPLATE_PREFIX, null, (state, action) => action.placeholder),
        networkEntityAddReducer(NetworkActionPrefix.CLONE_DASHBOARD_BLOCK_PREFIX, null, (state, _action) => state),
        networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_BLOCK_PREFIX, null, (state, action) => action.block),
        networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_BLOCK_PREFIX, null),
        block
    ),

    dashboards = pipeReducers(
        (state, action) => action.type === ActionType.EDIT_DASHBOARD_BLOCK ? dashboard(state, action) : state,
        combineReducers({
            blockTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_BLOCK_TYPES_PREFIX, defaults.NetworkArray, DashboardBlockType, 'DashboardPageBlockType'),
            cartesianSerieTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_CARTESIAN_SERIE_TYPES_PREFIX, defaults.NetworkArray, CartesianSerieType, 'DashboardPageBlockCartesianSerieType'),
            comparisonColumnSerieTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_COMPARISON_COLUMN_SERIE_TYPES_PREFIX, defaults.NetworkArray, ComparisonColumnSerieType, 'DashboardPageBlockComparisonColumnSerieType'),
            comparisonBarSerieTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_COMPARISON_BAR_SERIE_TYPES_PREFIX, defaults.NetworkArray, ComparisonBarSerieType, 'DashboardPageBlockComparisonBarSerieType'),
            serieOrders: synchronizableEnumReducer(NetworkActionPrefix.LOAD_SERIE_ORDERS_PREFIX, defaults.NetworkArray, DashboardPageBlockSerieOrder, 'DashboardPageBlockSerieOrder'),
            cartesianAxes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_CARTESIAN_AXES_PREFIX, defaults.NetworkArray, DashboardCartesianAxis, 'DashboardPageBlockCartesianAxis'),
            cartesianSerieMilestones: pipeReducers(
                networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_DASHBOARD_CARTESIAN_SERIE_MILESTONES_PREFIX, defaults.NetworkArrayOf(defaults.DashboardPageBlockCartesianSerieMilestone)),
                combineReducers({
                    Data: combineReducers({
                        '@cartesianSerieMilestoneIndex': pipeReducers(
                            networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_CARTESIAN_SERIE_MILESTONE_PREFIX, null, (state, action) => action.cartesianSerieMilestone),
                            networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_CARTESIAN_SERIE_MILESTONE_PREFIX, null, (state, action) => action.cartesianSerieMilestone),
                            networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_CARTESIAN_SERIE_MILESTONE_PREFIX, null))
                    }, keyVariables)
                }, keyVariables)),
            groups: pipeReducers(
                networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_DASHBOARD_GROUPS_PREFIX, defaults.NetworkArrayOf(defaults.DashboardGroup)),
                combineReducers({
                    Data: combineReducers({
                        '@groupIndex': pipeReducers(
                            networkEntityLoadReducer(NetworkActionPrefix.LOAD_DASHBOARD_GROUP_PREFIX),
                            networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_GROUP_PREFIX, null, (state, action) => action.dashboardGroup),
                            networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_GROUP_PREFIX, null, (state, action) => action.dashboardGroup),
                            networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_GROUP_PREFIX, null)
                        )
                    }, keyVariables)
                }, keyVariables)),
            pages: pipeReducers(
                networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_DASHBOARD_PAGES_PREFIX, defaults.NetworkArrayOf(defaults.DashboardPageReport)),
                pages,
                combineReducers({
                    Data: combineReducers({
                        '@pageIndex': pipeReducers(
                            networkEntityLoadReducer(NetworkActionPrefix.LOAD_DASHBOARD_PAGE_PREFIX),
                            combineReducers({
                                Blocks: pipeReducers(
                                    networkPaginationLoadReducer(NetworkActionPrefix.LOAD_ALL_DASHBOARD_PAGE_BLOCKS_PREFIX, defaults.NetworkPaginationSliceOf(defaults.DashboardPageBlockReport)),
                                    paginationSlicePushReducer(ActionType.ADD_DASHBOARD_BLOCK_SUCCESS, defaults.NetworkPaginationSliceOf(defaults.DashboardPageBlockReport)),
                                    paginationSlicePushReducer(ActionType.ADD_DASHBOARD_BLOCK_FROM_TEMPLATE_SUCCESS, defaults.NetworkPaginationSliceOf(defaults.DashboardPageBlockReport)),
                                    paginationSlicePushReducer(ActionType.CLONE_DASHBOARD_BLOCK_SUCCESS, defaults.NetworkPaginationSliceOf(defaults.DashboardPageBlockReport)),
                                    paginationSlicePopReducer(ActionType.DELETE_DASHBOARD_BLOCK_SUCCESS, defaults.NetworkPaginationSliceOf(defaults.DashboardPageBlockReport)),
                                    combineReducers({
                                        Data: combineReducers({
                                            '@blockIndex': blockReducer
                                        }, keyVariables)
                                    }, keyVariables)
                                ),
                                UserAccesses: pipeReducers(
                                    networkEntityAddAllReducer(NetworkActionPrefix.ADD_ALL_DASHBOARD_PAGE_USER_ACCESSES_PREFIX, null, (state, action) => action.userAccesses),
                                    networkEntityDeleteAllReducer(NetworkActionPrefix.DELETE_ALL_DASHBOARD_PAGE_USER_ACCESSES_PREFIX, null, (state, action) => action.userAccessIDs)
                                )
                            }, keyVariables),
                            networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_PAGE_PREFIX, null, (state, action) => action.page),
                            networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_PAGE_FROM_TEMPLATE_PREFIX, null, (state, action) => action.placeholder),
                            networkEntityAddReducer(NetworkActionPrefix.CLONE_DASHBOARD_PAGE_PREFIX, null, (state, _action) => state),
                            networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_PAGE_PREFIX, null, (state, action) => action.page),
                            networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_PAGE_PREFIX, null),
                            networkActionReducer(NetworkActionPrefix.STORE_DASHBOARD_PAGE_SCREENSHOT_PREFIX, null))
                    }, keyVariables)
                }, keyVariables),
                networkObjectVoidReducer(ActionType.VOID_ALL_DASHBOARD_PAGES, defaults.NetworkArray)
            ),
            pageCloneStatuses: pipeReducers(
                combineReducers({
                    '@pageCloneStatusIndex': pipeReducers(
                        networkEntityAddReducer(NetworkActionPrefix.CLONE_DASHBOARD_PAGE_PREFIX, null, (state, action) => ({
                            Data: null,
                            Custom: {
                                status: RequestStatus.UNSYNCED,
                                sourcePageID: action.sourcePageID,
                                targetCompanyID: action.targetCompanyID,
                                changes: []
                            }
                        }))
                    )
                }, keyVariables),
                (state, action) => {
                    switch (action.type) {
                        case ActionType.CLONE_DASHBOARD_PAGE_SUCCESS:
                            return action.targetCompanyID ?
                                deepAssign(state, {
                                    [state.findIndex(status => status.Custom.sourcePageID === action.sourcePageID && status.Custom.targetCompanyID === action.targetCompanyID)]: {
                                        Custom: { status: RequestStatus.SUCCEEDED }
                                    }
                                }) :
                                state.filter(status => status.Custom.sourcePageID !== action.sourcePageID);

                        case ActionType.CLONE_DASHBOARD_PAGE_FAIL:
                            return action.targetCompanyID ?
                                deepAssign(state, {
                                    [state.findIndex(status => status.Custom.sourcePageID === action.sourcePageID && status.Custom.targetCompanyID === action.targetCompanyID)]: {
                                        Custom: {
                                            status: RequestStatus.FAILED,
                                            errorMessage: action.error.response && action.error.response.body && action.error.response.body.UIMessage
                                        }
                                    }
                                }) :
                                state.filter(status => status.Custom.sourcePageID !== action.sourcePageID);

                        case ActionType.DELETE_ALL_DASHBOARD_PAGE_CLONE_STATUSES:
                            return state?.length ? state.filter(status => status.Custom.sourcePageID !== action.sourcePageID) : [];

                        default:
                            return state.length ? state : [];
                    }
                }
            ),
            alertBlocks: pipeReducers(
                (state, action) => {
                    const
                        newState = (state || []).copy(),
                        defaultSlice = defaults.NestedNetworkPaginationSliceOf(defaults.DashboardPageBlockReport);

                    if (action.type.startsWith('LOAD_PERFORMANCE_MANAGEMENT_ALERT_BLOCK_SLICES')) {
                        const
                            response = action.response || [],
                            alertIDs = action.type === ActionType.LOAD_PERFORMANCE_MANAGEMENT_ALERT_BLOCK_SLICES_SUCCESS ?
                                response.map(({ FatherID }) => FatherID) :
                                action.alertIDs;

                        for (let alertIndex = 0; alertIndex < alertIDs.length; alertIndex++) {
                            const
                                alertID = alertIDs[alertIndex],
                                stateIndex = newState.findIndex(({ FatherID }) => FatherID === alertID),

                                oldSlice = newState.find(({ FatherID }) => FatherID === alertID) || deepAssign(defaultSlice, { FatherID: alertID }),
                                newSlice = response.find(({ FatherID }) => FatherID === alertID),
                                reducer = networkPaginationLoadReducer(NetworkActionPrefix.LOAD_PERFORMANCE_MANAGEMENT_ALERT_BLOCK_SLICES_PREFIX, oldSlice),

                                pendingAction = action.type === ActionType.LOAD_PERFORMANCE_MANAGEMENT_ALERT_BLOCK_SLICES_SUCCESS ?
                                    { ...action, response: newSlice } :
                                    action;

                            newState[stateIndex < 0 ? newState.length : stateIndex] = reducer(oldSlice, pendingAction);
                        }
                    }

                    if (action.type.startsWith('LOAD_ALL_PERFORMANCE_MANAGEMENT_ALERTS') && action.payload.status === RequestStatus.SUCCEEDED) {
                        return action.payload.response.leftJoin(
                            newState,
                            ({ ID }) => ID,
                            ({ FatherID }) => FatherID,
                            (alert, batchSlice) => batchSlice || deepAssign(defaultSlice, { FatherID: alert.ID })
                        );
                    }

                    if (action.type.startsWith(action.type === 'ADD_PERFORMANCE_MANAGEMENT_ALERT') && action.payload.status === RequestStatus.SUCCEEDED) {
                        newState.push(deepAssign(defaultSlice, { FatherID: action.payload.response }));
                    }

                    if (Object.prototype.hasOwnProperty.call(action, 'pageID') && Object.prototype.hasOwnProperty.call(action, 'blockID') && !action.pageID) {
                        return newState.map((alertBlocks) => {
                            const
                                data = alertBlocks.Data,
                                blockIndex = data.getIndexByID(action.blockID);

                            if (blockIndex < 0) { return alertBlocks; }

                            return deepAssign(alertBlocks, {
                                Data: {
                                    [blockIndex]: blockReducer(data[blockIndex], action)
                                }
                            });
                        });
                    }

                    return newState;
                }
            ),
            blockCloneStatuses: pipeReducers(
                combineReducers({
                    '@blockCloneStatusIndex': pipeReducers(
                        networkEntityAddReducer(NetworkActionPrefix.CLONE_DASHBOARD_BLOCK_PREFIX, null, (state, action) => ({
                            Data: null,
                            Custom: {
                                status: RequestStatus.UNSYNCED,
                                sourcePageID: action.sourcePageID,
                                sourceBlockID: action.sourceBlockID,
                                targetCompanyID: action.targetCompanyID,
                                changes: []
                            }
                        }))
                    )
                }, keyVariables),
                (state, action) => {
                    switch (action.type) {
                        case ActionType.CLONE_DASHBOARD_BLOCK_SUCCESS:
                            return action.targetCompanyID ?
                                deepAssign(state, {
                                    [state.findIndex(status => status.Custom.sourcePageID === action.sourcePageID && status.Custom.sourceBlockID === action.sourceBlockID && status.Custom.targetCompanyID === action.targetCompanyID)]: {
                                        Custom: { status: RequestStatus.SUCCEEDED }
                                    }
                                }) :
                                state.filter(status => status.Custom.sourcePageID !== action.sourcePageID || status.Custom.sourceBlockID !== action.sourceBlockID);

                        case ActionType.CLONE_DASHBOARD_BLOCK_FAIL:
                            return action.targetCompanyID ?
                                deepAssign(state, {
                                    [state.findIndex(status => status.Custom.sourcePageID === action.sourcePageID && status.Custom.sourceBlockID === action.sourceBlockID && status.Custom.targetCompanyID === action.targetCompanyID)]: {
                                        Custom: {
                                            status: RequestStatus.FAILED,
                                            errorMessage: action.error.response && action.error.response.body && action.error.response.body.UIMessage
                                        }
                                    }
                                }) :
                                state.filter(status => status.Custom.sourcePageID !== action.sourcePageID || status.Custom.sourceBlockID !== action.sourceBlockID);

                        case ActionType.DELETE_ALL_DASHBOARD_BLOCK_CLONE_STATUSES:
                            return state?.length ?
                                state.filter(status => status.Custom.sourcePageID !== action.sourcePageID || status.Custom.sourceBlockID !== action.sourceBlockID) :
                                [];

                        default:
                            return state.length ? state : [];
                    }
                }
            ),
            homeSettings: pipeReducers(
                networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_DASHBOARD_PAGE_HOME_SETTINGSES_PREFIX, defaults.NetworkArrayOf(defaults.DAshboardPageHomeSettings)),
                combineReducers({
                    Data: combineReducers({
                        '@homeSettingsIndex': pipeReducers(
                            networkEntityLoadReducer(NetworkActionPrefix.LOAD_DASHBOARD_PAGE_HOME_SETTINGS_PREFIX),
                            networkEntityAddReducer(NetworkActionPrefix.ADD_DASHBOARD_PAGE_HOME_SETTINGS_PREFIX, null, (state, action) => action.dashboardPageHomeSettings),
                            networkEntityEditReducer(NetworkActionPrefix.EDIT_DASHBOARD_PAGE_HOME_SETTINGS_PREFIX, null, (state, action) => action.dashboardPageHomeSettings),
                            networkEntityDeleteReducer(NetworkActionPrefix.DELETE_DASHBOARD_PAGE_HOME_SETTINGS_PREFIX)
                        )
                    }, keyVariables)
                }, keyVariables)),
            // TODO remove duplication, currently it's a copy of performanceIndicators.relations to make it available in block reducer
            indicators: pipeReducers(
                networkObjectLoadReducer(NetworkActionPrefix.LOAD_ALL_PERFORMANCE_INDICATORS_PREFIX, defaults.NetworkArray),
                (state, action) => {
                    switch (action.type) {
                    // TODO synchronize indicator list, relation reports and overall dashboards data more precisely
                        case ActionType.ADD_PERFORMANCE_INDICATOR_SUCCESS:
                        case ActionType.EDIT_PERFORMANCE_INDICATOR:
                        case ActionType.DELETE_PRODUCT_SUCCESS:
                        case ActionType.DELETE_GEO_AREA_SUCCESS:
                        case ActionType.DELETE_CUSTOMER_SUCCESS:
                        case ActionType.DELETE_TRACKING_LEVEL_1_ITEM_SUCCESS:
                        case ActionType.DELETE_TRACKING_LEVEL_2_ITEM_SUCCESS:
                        case ActionType.DELETE_TRACKING_LEVEL_3_ITEM_SUCCESS:
                        case ActionType.DELETE_TRACKING_LEVEL_4_ITEM_SUCCESS:
                            return defaults.NetworkArray;

                        default:
                            return state;
                    }
                }),
            indicatorsData: pipeReducers(
                resetIndicatorsDataReducer,
                (state, action) => {
                    let droppedIDs = [];

                    switch (action.type) {
                        case ActionType.SEARCH_DASHBOARD_INDICATOR_DATA_PENDING:
                        case ActionType.SEARCH_DASHBOARD_INDICATOR_DATA_SUCCESS:
                        case ActionType.SEARCH_DASHBOARD_INDICATOR_DATA_FAIL:

                            const tempState = [...state];

                            return deepAssign(
                                state,
                                (action.response || action.templates)
                                    .reduce((tempState, newData) => {
                                        newData = { ...defaults.PerformanceIndicatorDataReport, ...newData, ID: newData.ID };
                                        newData.ID = newData.ID || indicatorReportID(newData.IndicatorID, {
                                            PeriodTypeID: newData.PeriodTypeID || null,
                                            CorporateStructureLink: newData.CorporateStructureLink || defaults.CorporateStructureLink
                                        });
                                        let oldData = tempState.getByID(newData.ID);
                                        if (oldData && newData) {
                                            if (action.response) {
                                                newData.Data = [
                                                    ...oldData.Data.filter(item => newData.StartDate && newData.StartDate >= item.Date || newData.EndDate && newData.EndDate <= item.Date),
                                                    ...newData.Data
                                                ].sortBy(data => +data.Date);
                                            }
                                            else {
                                                newData.Data = oldData.Data;
                                            }

                                            const startDate = !oldData.StartDate || !newData.StartDate ? null : oldData.StartDate < newData.StartDate ? oldData.StartDate : newData.StartDate || null,
                                                endDate = !oldData.EndDate || !newData.EndDate ? null : oldData.EndDate > newData.EndDate ? oldData.EndDate : newData.EndDate || null;
                                            newData.HasActual = oldData.HasActual || newData.HasActual;
                                            newData.HasBudget = oldData.HasBudget || newData.HasBudget;
                                            newData.HasForecast = oldData.HasForecast || newData.HasForecast;
                                            newData.HasGoal = oldData.HasGoal || newData.HasGoal;
                                            newData.ValueDependencyRuleID = newData.ValueDependencyRuleID || oldData.ValueDependencyRuleID;
                                            newData.NegateFinancialValue = oldData.NegateFinancialValue || newData.NegateFinancialValue;
                                            newData.ValueSuffix = oldData.ValueSuffix || newData.ValueSuffix;
                                            newData.LastActualUpdate = !oldData.LastActualUpdate ? newData.LastActualUpdate : !newData.LastActualUpdate ? oldData.LastActualUpdate : oldData.LastActualUpdate <= newData.LastActualUpdate ? newData.LastActualUpdate : oldData.LastActualUpdate;

                                            let loadedRanges = deepClone(oldData.Custom.loadedRanges);
                                            if (!loadedRanges || !loadedRanges.length || !startDate && !endDate) {
                                                loadedRanges = [{ start: startDate, end: endDate, status: action.response ? RequestStatus.SUCCEEDED : RequestStatus.PENDING }];
                                            }
                                            else {
                                            // TODO optimize this stuff
                                                loadedRanges = loadedRanges
                                                    .map((range, index, ranges) => [
                                                        {
                                                            date: range.start || -Infinity,
                                                            left: ranges[index - 1] && (ranges[index - 1].end || Infinity) >= (range.start || -Infinity) ? ranges[index - 1].status : RequestStatus.UNSYNCED,
                                                            right: range.status
                                                        },
                                                        {
                                                            date: range.end || Infinity,
                                                            left: range.status,
                                                            right: ranges[index + 1] && (ranges[index + 1].start || -Infinity) <= (range.end || -Infinity) ? ranges[index + 1].status : RequestStatus.UNSYNCED
                                                        }
                                                    ])
                                                    .flatten()
                                                    .filter(({ date }) => date < (newData.StartDate || -Infinity) || date > (newData.EndDate || Infinity))
                                                // TODO fill all the holes in the new range with PENDING/SUCCEEDED, not only one; for now remove all not-holes (line above)
                                                    .concat([
                                                        {
                                                            date: newData.StartDate || -Infinity,
                                                            left: RequestStatus.UNSYNCED,
                                                            right: action.response ? RequestStatus.SUCCEEDED : RequestStatus.PENDING
                                                        },
                                                        {
                                                            date: newData.EndDate || Infinity,
                                                            left: action.response ? RequestStatus.SUCCEEDED : RequestStatus.PENDING,
                                                            right: RequestStatus.UNSYNCED
                                                        }
                                                    ])
                                                    .sortBy(({ date }) => date)
                                                    .groupBy(({ date }) => +date)
                                                    .map(group => ({
                                                        date: group[0].date,
                                                        left: group.reduce((r, { left }) => r === RequestStatus.SUCCEEDED || left === RequestStatus.SUCCEEDED ? RequestStatus.SUCCEEDED :
                                                        r === RequestStatus.PENDING || left === RequestStatus.PENDING ? RequestStatus.PENDING :
                                                        RequestStatus.UNSYNCED, group[0].left),
                                                        right: group.reduce((r, { right }) => r === RequestStatus.SUCCEEDED || right === RequestStatus.SUCCEEDED ? RequestStatus.SUCCEEDED :
                                                        r === RequestStatus.PENDING || right === RequestStatus.PENDING ? RequestStatus.PENDING :
                                                        RequestStatus.UNSYNCED, group[0].right)
                                                    }))
                                                    .map((point, index, points) => ({
                                                        start: point.date,
                                                        status: point.right === RequestStatus.SUCCEEDED || (points[index + 1] ? points[index + 1].left : point.right) === RequestStatus.SUCCEEDED ? RequestStatus.SUCCEEDED :
                                                        point.right === RequestStatus.PENDING || (points[index + 1] ? points[index + 1].left : point.right) === RequestStatus.PENDING ? RequestStatus.PENDING :
                                                        RequestStatus.UNSYNCED,
                                                        end: points[index + 1] ? points[index + 1].date : point.date
                                                    }))
                                                    .slice(0, -1)
                                                    .filter(range => range.start < range.end && range.status !== RequestStatus.UNSYNCED);
                                                for (let i = 0; i < loadedRanges.length; i++) {
                                                    if (loadedRanges[i] && loadedRanges[i + 1] && loadedRanges[i].status === loadedRanges[i + 1].status && loadedRanges[i].end >= loadedRanges[i + 1].start) {
                                                        loadedRanges[i].end = loadedRanges[i + 1].end;
                                                        loadedRanges.splice(i + 1, 1);
                                                        i--;
                                                    }
                                                }
                                            }

                                            newData.StartDate = startDate;
                                            newData.EndDate = endDate;
                                            newData.Custom.loadedRanges = loadedRanges;

                                            oldData = deepAssign(oldData, { Custom: { loadedRanges } });
                                        }
                                        else if (!newData.Custom.loadedRanges.length) {
                                            newData.Custom.loadedRanges = [{ start: newData.StartDate, end: newData.EndDate, status: action.response ? RequestStatus.SUCCEEDED : RequestStatus.PENDING }];
                                        }

                                        const newItem = optimisticNetworkEntityLoadReducer(NetworkActionPrefix.SEARCH_DASHBOARD_INDICATOR_DATA_PREFIX, defaults.PerformanceIndicatorDataReport, () => newData.ID)(oldData || newData, { ...action, response: newData }),

                                            index = tempState.getIndexByID(newData.ID);
                                        if (index !== -1) {
                                            tempState[index] = newItem;
                                        }
                                        else {
                                            tempState.push(newItem);
                                        }

                                        return tempState;
                                    }, tempState)
                                    .sortBy(({ ID }) => ID)
                            );

                        case ActionType.MERGE_ALL_PERFORMANCE_INDICATOR_DAY_DATA_SUCCESS:
                            droppedIDs = action.entities
                                .map(entity => indicatorReportID(entity.KeyPerformanceIndicatorID, { CorporateStructureLink: entity.CorporateStructureLink, PeriodTypeID: PeriodType.DAY }))
                                .unique();

                            return state.filter(data => !droppedIDs.includes(data.ID));

                        case ActionType.MERGE_ALL_PERFORMANCE_INDICATOR_WEEK_DATA_SUCCESS:
                            droppedIDs = action.entities
                                .map(entity => indicatorReportID(entity.KeyPerformanceIndicatorID, { CorporateStructureLink: entity.CorporateStructureLink, PeriodTypeID: PeriodType.WEEK }))
                                .unique();

                            return state.filter(data => !droppedIDs.includes(data.ID));

                        case ActionType.MERGE_ALL_PERFORMANCE_INDICATOR_MONTH_DATA_PENDING:
                            droppedIDs = action.entities
                                .map(entity => indicatorReportID(entity.KeyPerformanceIndicatorID, { CorporateStructureLink: entity.CorporateStructureLink, PeriodTypeID: PeriodType.MONTH }))
                                .unique();

                            return deepAssign(state, state.map(data => !droppedIDs.includes(data.ID) ? data : { Custom: { status: RequestStatus.PENDING } }));

                        case ActionType.MERGE_ALL_PERFORMANCE_INDICATOR_MONTH_DATA_SUCCESS:
                            droppedIDs = action.entities
                                .map(entity => indicatorReportID(entity.KeyPerformanceIndicatorID, { CorporateStructureLink: entity.CorporateStructureLink, PeriodTypeID: PeriodType.MONTH }))
                                .unique();

                            return state.filter(data => !droppedIDs.includes(data.ID));

                        case ActionType.MERGE_ALL_PERFORMANCE_INDICATOR_QUARTER_DATA_SUCCESS:
                            droppedIDs = action.entities
                                .map(entity => indicatorReportID(entity.KeyPerformanceIndicatorID, { CorporateStructureLink: entity.CorporateStructureLink, PeriodTypeID: PeriodType.QUARTER }))
                                .unique();

                            return state.filter(data => !droppedIDs.includes(data.ID));

                        case ActionType.MERGE_ALL_PERFORMANCE_INDICATOR_YEAR_DATA_SUCCESS:
                            droppedIDs = action.entities
                                .map(entity => indicatorReportID(entity.KeyPerformanceIndicatorID, { CorporateStructureLink: entity.CorporateStructureLink, PeriodTypeID: PeriodType.YEAR }))
                                .unique();

                            return state.filter(data => !droppedIDs.includes(data.ID));

                        default:
                            return state;
                    }
                }
            ),
            indicatorsDrillData: combineReducers({
                '@indicatorsDrillDataKey': optimisticNetworkEntityLoadReducer(NetworkActionPrefix.SEARCH_DASHBOARD_INDICATOR_DRILL_DATA_PREFIX, defaults.PerformanceIndicatorDrillData, (state, action) => indicatorDrillDataID(action.filters))
            }, keyVariables),
            indicatorsAvailability: pipeReducers(
                resetIndicatorsDataReducer,
                (state, action) => {
                    switch (action.type) {
                        case ActionType.SEARCH_DASHBOARD_INDICATOR_AVAILABILITY_PENDING:
                        case ActionType.SEARCH_DASHBOARD_INDICATOR_AVAILABILITY_SUCCESS:
                        case ActionType.SEARCH_DASHBOARD_INDICATOR_AVAILABILITY_FAIL:
                            const
                                oldDatas = action.templates.concat(action.response || []).map(oldData => state.getByID(oldData.ID || indicatorReportID(oldData.IndicatorID, {
                                    PeriodTypeID: null,
                                    CorporateStructureLink: oldData.CorporateStructureLink || defaults.CorporateStructureLink
                                }))),
                                newDatas = (action.response || action.templates)
                                    .map((newData) => {
                                        const id = newData.ID || indicatorReportID(newData.IndicatorID, {
                                            PeriodTypeID: null,
                                            CorporateStructureLink: newData.CorporateStructureLink || defaults.CorporateStructureLink
                                        });

                                        return optimisticNetworkEntityLoadReducer(NetworkActionPrefix.SEARCH_DASHBOARD_INDICATOR_AVAILABILITY_PREFIX, defaults.PerformanceIndicatorAvailabilityReport, () => id)(oldDatas.getByID(id) || { ...defaults.PerformanceIndicatorAvailabilityReport, ...newData }, { ...action, response: newData });
                                    })
                                    .filter((newData, index, array) => newData && array.getIndexByID(newData.ID) === index);

                            return state
                                .filter(oldData => !oldDatas.includes(oldData))
                                .concat(newDatas);

                        default:
                            return state;
                    }
                }
            ),
            dashboardStrategicActionViewTypes: synchronizableEnumReducer(NetworkActionPrefix.LOAD_ALL_DASHBOARD_STRATEGIC_ACTION_VIEW_TYPES_PREFIX, defaults.NetworkArray, DashboardStrategicActionViewType, DashboardStrategicActionViewType),
            mapDataReport: pipeReducers(
                networkEntityLoadReducer(NetworkActionPrefix.LOAD_MAP_DATA_REPORT_PREFIX, defaults.MapDataReport),
                combineReducers({
                    Regions: combineReducers({
                        '@regionIndex': combineReducers({
                            Invoices: networkPaginationLoadReducer(NetworkActionPrefix.SEARCH_MAP_DATA_REPORT_INVOICES_SLICE_PREFIX, defaults.PaginationSlice)
                        }, keyVariables)
                    }, keyVariables)
                }, keyVariables),
                (state, action) => action.type === ActionType.VOID_MAP_DATA_REPORT ? defaults.MapDataReport : state
            )
        }, keyVariables),
        (state, action) => action.type !== ActionType.EDIT_DASHBOARD_BLOCK ? dashboard(state, action) : state,
        (state, action) => {
            switch (action.type) {
                case ActionType.VOID_CORPORATE_STRUCTURE:
                case ActionType.DELETE_PRODUCT_SUCCESS:
                case ActionType.DELETE_GEO_AREA_SUCCESS:
                case ActionType.DELETE_CUSTOMER_SUCCESS:
                case ActionType.DELETE_TRACKING_LEVEL_1_ITEM_SUCCESS:
                case ActionType.DELETE_TRACKING_LEVEL_2_ITEM_SUCCESS:
                case ActionType.DELETE_TRACKING_LEVEL_3_ITEM_SUCCESS:
                case ActionType.DELETE_TRACKING_LEVEL_4_ITEM_SUCCESS:
                case ActionType.IMPORT_AND_PROCESS_EXCEL_FILE_SUCCESS:
                case ActionType.DELETE_POLL_SETTINGS_SUCCESS:
                // TODO make it more targeted, not all parts require update
                    return dashboards(undefined, { type: '@@redux/INIT' });
                default:
                    return state;
            }
        }
    );

export default dashboards;
